<template>
  <div class="approval-card">
    <div class="approval-card__label">{{ introLabel }}</div>

    <template v-for="(group, groupIdx) in actionGroups" :key="groupIdx">
      <div class="approval-card__label">{{ formatKey(group.name) }}</div>
      <div v-if="group.terms.length" class="approval-card__terms">
        <div v-for="(term, termIdx) in group.terms" :key="termIdx" class="approval-card__term">
          <BbIcon size="small" name="search" />
          <span>{{ term }}</span>
        </div>
      </div>
    </template>

    <div class="approval-card__footer">
      <div class="approval-card__note">
        <BbIcon size="small" name="information" />
        <span>{{ t('conversation.approval.complianceNote') }}</span>
      </div>

      <!-- Default: decision buttons -->
      <div v-if="!state.mode" class="approval-card__decisions">
        <BbBtn
          v-for="decision in allowedDecisions"
          :key="decision"
          size="small"
          :type="decision === APPROVAL_DECISION.REJECT ? 'secondary' : 'primary'"
          @click="handleDecision(decision)"
        >
          {{ getDecisionButtonLabel(decision) }}
        </BbBtn>
      </div>

      <!-- Edit form -->
      <div v-else-if="state.mode === APPROVAL_DECISION.EDIT" class="interrupt-block__form">
        <textarea
          v-model="state.editedArgs"
          class="interrupt-block__textarea interrupt-block__textarea--code"
          rows="8"
          spellcheck="false"
        />
        <div v-if="state.editError" class="interrupt-block__error">
          {{ state.editError }}
        </div>
        <div class="interrupt-block__form-actions">
          <BbBtn size="small" type="secondary" @click="cancelMode">Cancel</BbBtn>
          <BbBtn size="small" type="primary" @click="submitEdit">Submit Edit</BbBtn>
        </div>
      </div>

      <!-- Respond form -->
      <div v-else-if="state.mode === APPROVAL_DECISION.RESPOND" class="interrupt-block__form">
        <textarea v-model="state.message" placeholder="Your message..." class="interrupt-block__textarea" rows="3" />
        <div class="interrupt-block__form-actions">
          <BbBtn size="small" type="secondary" @click="cancelMode">Cancel</BbBtn>
          <BbBtn
            size="small"
            type="primary"
            @click="submitDecision(buildDecisions({ type: APPROVAL_DECISION.RESPOND, message: state.message }))"
          >
            Send
          </BbBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  APPROVAL_DECISION,
  APPROVAL_DECISION_BUTTON_LABEL_KEYS,
  APPROVAL_HIDDEN_ARG_LABELS,
  RESUME_COMMAND_TYPE,
} from '~/constants/conversationModels';

import useConversations from '~/composables/useConversations';
import { formatKey } from '~/utils/format';

// The search term a request asks for: its argument values (arrays joined), without the hidden ones.
const formatTerm = (args) => {
  if (!args || typeof args !== 'object') return '';
  return Object.entries(args)
    .filter(([key]) => !APPROVAL_HIDDEN_ARG_LABELS.includes(formatKey(key)))
    .map(([, value]) => {
      if (Array.isArray(value)) return value.join(', ');
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    })
    .filter(Boolean)
    .join(' · ');
};

// The HITL tool-review half of what used to be InterruptBlockSSE.vue's single component.
// The image-gen half is ImageStyleChoiceInterrupt.vue. One instance of this component per
// interrupt (via InterruptBlockSSE's v-for), so form state (mode/reason/etc.) is local —
// no more manually-keyed-by-id reactive map.
export default {
  name: 'HitlReviewInterrupt',
  props: {
    interrupt: {
      type: Object,
      required: true,
    },
  },
  emits: ['submitted'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { resumeConversation } = useConversations();

    const state = reactive({
      mode: null,
      editedArgs: '',
      editError: '',
      message: '',
    });

    const allowedDecisions = computed(
      () => props.interrupt.value?.review_configs?.[0]?.allowed_decisions ?? [APPROVAL_DECISION.APPROVE],
    );

    // Consecutive requests for the same tool share one name with a search term row each, e.g. three
    // PubMed searches requested at once.
    const actionGroups = computed(() =>
      (props.interrupt.value?.action_requests ?? []).reduce((groups, action) => {
        const term = formatTerm(action.args);
        const lastGroup = groups[groups.length - 1];
        if (lastGroup?.name === action.name) {
          if (term) lastGroup.terms.push(term);
        } else {
          groups.push({ name: action.name, terms: term ? [term] : [] });
        }
        return groups;
      }, []),
    );

    const introLabel = computed(() => {
      const termCount = actionGroups.value.reduce((count, group) => count + group.terms.length, 0);
      return t(termCount > 1 ? 'conversation.approval.allowArcaneToSearch' : 'conversation.approval.allowArcaneTo');
    });

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const getDecisionButtonLabel = (decision) =>
      APPROVAL_DECISION_BUTTON_LABEL_KEYS[decision]
        ? t(APPROVAL_DECISION_BUTTON_LABEL_KEYS[decision])
        : capitalize(decision);

    const cancelMode = () => {
      state.mode = null;
      state.editError = '';
    };

    // Builds a decisions array with one entry per action_request so backend
    // count matches hanging tool calls. Edit decisions use per-action name.
    const buildDecisions = (decision) => {
      const requests = props.interrupt.value?.action_requests ?? [];
      if (requests.length <= 1) return [decision];
      if (decision.type === APPROVAL_DECISION.EDIT) {
        return requests.map((action) => ({
          type: APPROVAL_DECISION.EDIT,
          editedAction: { name: action.name, args: decision.editedAction.args },
        }));
      }
      return requests.map(() => ({ ...decision }));
    };

    // Emit before awaiting: collapses the parent's panel to its pill immediately
    // and unmounts this form (prevents double-submit too).
    const submitDecision = async (decisions) => {
      emit('submitted', decisions[0]?.type ?? '');
      await resumeConversation({ type: RESUME_COMMAND_TYPE.HITL_RESPONSE, decisions });
    };

    // Approve and Cancel (reject) submit right away; edit and respond open their form first.
    const handleDecision = (decision) => {
      if (decision === APPROVAL_DECISION.APPROVE || decision === APPROVAL_DECISION.REJECT) {
        submitDecision(buildDecisions({ type: decision }));
      } else if (decision === APPROVAL_DECISION.EDIT) {
        // not exposing decisions in from arcane-ai yet (for hitlRequest)
        const args = props.interrupt.value?.action_requests?.[0]?.args ?? {};
        state.editedArgs = JSON.stringify(args, null, 2);
        state.mode = APPROVAL_DECISION.EDIT;
      } else {
        state.mode = decision;
      }
    };

    const submitEdit = () => {
      // given we are not renderin raw JSON to the user to edit anymore, this will need to be reworked if arcane-ai ever sends HITL interrupts with the edit action.
      let parsed;
      try {
        parsed = JSON.parse(state.editedArgs);
        state.editError = '';
      } catch {
        state.editError = 'Invalid JSON — fix before submitting.';
        return;
      }
      const actionName = props.interrupt.value?.action_requests?.[0]?.name ?? '';
      submitDecision(
        buildDecisions({ type: APPROVAL_DECISION.EDIT, editedAction: { name: actionName, args: parsed } }),
      );
    };

    return {
      t,
      APPROVAL_DECISION,
      state,
      allowedDecisions,
      actionGroups,
      introLabel,
      formatKey,
      getDecisionButtonLabel,
      cancelMode,
      buildDecisions,
      handleDecision,
      submitDecision,
      submitEdit,
    };
  },
};
</script>

<style scoped lang="scss">
.approval-card {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
  padding: $bb-spacing-5;
  background-color: $bb-neutral-0;
  border: 1px solid $bb-neutral-4;
  border-radius: 16px;
  color: $bb-neutral-1;
}

.approval-card__label {
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.1px;
}

.approval-card__terms {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
}

.approval-card__term {
  display: flex;
  align-items: center;
  gap: $bb-spacing-2;
  min-height: 30px;
  padding: 6px $bb-spacing-3;
  background-color: $bb-neutral-6;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  line-height: 16px;
  word-break: break-word;

  :deep(.bb-icon.small) {
    flex-shrink: 0;
    font-size: 18px;
  }
}

.approval-card__footer {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;
}

.approval-card__note {
  display: flex;
  align-items: center;
  gap: $bb-spacing-1;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.2px;

  :deep(.bb-icon) {
    flex-shrink: 0;
  }
}

.approval-card__decisions {
  display: flex;
  flex-wrap: wrap;
  gap: $bb-spacing-2;

  :deep(.bb-btn.v-btn--size-small) {
    height: 40px;
  }
}

.interrupt-block__form {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
}

.interrupt-block__form-actions {
  display: flex;
  gap: $bb-spacing-2;
  justify-content: flex-end;
}

.interrupt-block__textarea {
  width: 100%;
  border: 1px solid $bb-neutral-4;
  border-radius: 6px;
  padding: $bb-spacing-2 $bb-spacing-3;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  background-color: $bb-neutral-6;
  color: $bb-neutral-1;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: $bb-primary-3;
  }

  &--code {
    font-family: monospace;
    font-size: 12px;
  }
}

.interrupt-block__error {
  @include bb-regular-text-styles('sm');
  color: #ef4444;
}
</style>
